import { formatDateTime, formatPhone, TextButton } from "@majitap/ui";
import { useState } from "react";
import { useCompanyFormat } from "../../company";
import type { DeliveryRecord } from "../../deliveries";
import { DELIVERY_STATUS, type Tone } from "../../labels";
import { RouterLink } from "../../router-link";
import { LogIncidentDialog, ResolveDialog } from "../incidents/dialogs";
import { Facts, SideCard } from "../orders/order-side";
import page from "../orders/record.module.css";

/** Where it goes and who to call there. */
export function DeliverToCard({ delivery: d }: { delivery: DeliveryRecord }) {
  const place = d.location;
  const floor = place?.floor !== null && place?.floor !== undefined ? `floor ${place.floor}` : null;
  return (
    <SideCard
      title="Deliver to"
      link={
        <RouterLink href={`/orders/${d.order.id}`} className={page.link}>
          {d.order.order_number}
        </RouterLink>
      }
    >
      <Facts
        rows={[
          ["Customer", [d.order.customer.name, d.order.business?.name].filter(Boolean).join(" · ")],
          ["Place", place ? [place.label, place.address_line, floor].filter(Boolean).join(" · ") : null],
          ["Landmark", place?.landmark],
          [
            "Contact",
            place?.contact_name || place?.contact_phone
              ? [place.contact_name, formatPhone(place.contact_phone)].filter(Boolean).join(" · ")
              : null,
          ],
          ["Notes", d.notes],
          ["Zone", d.geofence?.name ?? "Outside every zone"],
        ]}
      />
    </SideCard>
  );
}

/** Its incidents: open ones can be resolved here; staff can record a new one. */
export function IncidentsCard({ delivery: d, canAct }: { delivery: DeliveryRecord; canAct: boolean }) {
  const format = useCompanyFormat();
  const [logging, setLogging] = useState(false);
  const [resolving, setResolving] = useState<DeliveryRecord["incidents"][number] | null>(null);
  const canLog = canAct && d.status !== "rerouted" && d.status !== "cancelled";
  return (
    <SideCard
      title="Incidents"
      link={canLog ? <TextButton onClick={() => setLogging(true)}>Record one</TextButton> : undefined}
    >
      {d.incidents.length === 0 ? <p className={page.muted}>None reported.</p> : null}
      {d.incidents.map((i) => (
        <div key={i.id} className={page.stackGap}>
          <div className={page.cardHead}>
            <strong>{i.type.name}</strong>
            <span className={page.small}>{formatDateTime(i.reported_at, format)}</span>
          </div>
          <span className={page.small}>
            {[i.notes, i.reported_by ? `Reported by ${i.reported_by.name}.` : null].filter(Boolean).join(" ")}
          </span>
          {i.resolved_at ? (
            <span className={page.small}>
              Resolved {formatDateTime(i.resolved_at, format)}
              {i.resolution_notes ? `: ${i.resolution_notes}` : ""}
            </span>
          ) : canAct ? (
            <TextButton onClick={() => setResolving(i)}>Resolve with notes</TextButton>
          ) : null}
        </div>
      ))}
      {logging ? <LogIncidentDialog delivery={d} onClose={() => setLogging(false)} /> : null}
      {resolving ? (
        <ResolveDialog
          incident={{ ...resolving, delivery_number: d.delivery_number }}
          onClose={() => setResolving(null)}
        />
      ) : null}
    </SideCard>
  );
}

const DOT: Record<Tone, string | undefined> = {
  neutral: page.eventDotMuted,
  info: page.eventDotInfo,
  success: page.eventDotSuccess,
  warning: page.eventDotWarning,
  danger: page.eventDotDanger,
};

/** Where the truck was when the driver marked a step (P12-26), or why that couldn't be checked. */
function arrivalText(a: DeliveryRecord["events"][number]["arrival"]) {
  if (!a) return null;
  const where = a.place === "fillsite" ? "the fill site" : "the place";
  if (a.checked) return `${a.distance_m.toLocaleString("en-GB")} m from ${where}`;
  return a.why === "no_pin"
    ? `Location not checked: ${where} has no map pin`
    : "Location not checked: no recent position from the truck";
}

/** Every status change, newest first, with who made it and why. */
export function TimelineCard({ delivery: d }: { delivery: DeliveryRecord }) {
  const format = useCompanyFormat();
  return (
    <SideCard title="Timeline">
      <ol className={page.timeline}>
        {/* moves to another time (P8-75) */}
        {d.reschedules.map((r) => (
          <li key={`moved-${r.at}`} className={page.event}>
            <span className={page.eventDot} aria-hidden />
            <span>
              {`Rescheduled to ${formatDateTime(r.to, format)}`}
              <br />
              <span className={page.small}>
                {[
                  r.by?.name ?? "The system",
                  r.from ? `was ${formatDateTime(r.from, format)}` : "was as soon as possible",
                  r.reason,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <span className={page.small}>{formatDateTime(r.at, format)}</span>
          </li>
        ))}
        {d.events.map((e) => {
          const [status, tone] = DELIVERY_STATUS[e.to_status];
          const label = e.from_status === null ? `Created with ${d.order.order_number}` : status;
          return (
            <li key={e.id} className={page.event}>
              <span className={`${page.eventDot} ${DOT[tone] ?? ""}`} aria-hidden />
              <span>
                {label}
                <br />
                <span className={page.small}>
                  {[e.actor?.name ?? "The system", e.reason].filter(Boolean).join(" · ")}
                </span>
                {e.arrival ? (
                  <>
                    <br />
                    <span className={e.arrival.checked ? page.small : `${page.small} ${page.warning}`}>
                      {arrivalText(e.arrival)}
                    </span>
                  </>
                ) : null}
              </span>
              <span className={page.small}>{formatDateTime(e.occurred_at, format)}</span>
            </li>
          );
        })}
        {d.events.some((e) => e.from_status === null) ? null : (
          <li className={page.event}>
            <span className={page.eventDot} aria-hidden />
            <span>Created with {d.order.order_number}</span>
            <span className={page.small}>{formatDateTime(d.created_at, format)}</span>
          </li>
        )}
      </ol>
    </SideCard>
  );
}
